import { cloneDeep, toInteger } from 'lodash';
import { getFramework } from '@/framework';
import {
    createCanvas,
    EmptyPointImage,
    fetchVectorText,
    getVectorTextFromCache,
    parseColorToRgba,
    parseStrokeDasharray,
} from '@/utils/file-kit';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { calculateNewCoordinates, getPointsBetweenAngle } from '@/utils/math-kit';
import {
    MarkupPointType,
    MarkupTextPositionType,
    MarkupType,
    type ILayerProps,
    type IMarkupLayerProps,
} from '@/version-types';
import { prepareMarkupLayerDocumentFonts } from '../font-measurement';
import { fixDrawResult } from './attr';
import { drawMarkupLabelToCanvas } from './label-canvas';
import LabelKit from './label-kit';
import { createMarkupLabelLayout, type IMarkupLabelLayout } from './label-layout';
import { getPointImage, MARKUP_POINT_SIZE } from './layer';

interface IPointImage {
    width: number;
    height: number;
    element: {
        tag: string;
        attrs: Record<string, unknown>;
    };
}

function getRgbColor(color: string) {
    const rgba = parseColorToRgba(color);
    return {
        rgba,
        rgb: `rgb(${rgba.r},${rgba.g},${rgba.b})`,
    };
}

function setStrokeDash(ctx: CanvasRenderingContext2D, dasharray: string, fillWidth: number) {
    const dasharrayStr = parseStrokeDasharray(dasharray, fillWidth) as string;
    const dasharrayList = dasharrayStr
        ? dasharrayStr
              .split(',')
              .map((item) => Number(item))
              .filter((item) => !Number.isNaN(item))
        : [];
    ctx.setLineDash(dasharrayList);
}

function drawLineRect(ctx: CanvasRenderingContext2D, arrowAnchor: number[], x: number, y: number, fillWidth: number) {
    const rectHeight = Math.max(0, Math.max(arrowAnchor?.[0], 0) - arrowAnchor?.[1]);
    ctx.fillRect(x, y, fillWidth, rectHeight);
}

function drawPointImage(ctx: CanvasRenderingContext2D, pointImg: IPointImage) {
    const { tag, attrs } = pointImg.element;
    if (!tag) return;

    if (tag === 'circle') {
        ctx.beginPath();
        ctx.arc(Number(attrs.cx || 0), Number(attrs.cy || 0), Number(attrs.r || 0), 0, Math.PI * 2);
        ctx.fill();
        return;
    }

    const path = new Path2D(String(attrs.d || ''));
    const fill = attrs.fill;
    const shouldStroke = fill === 'none' || Number(attrs.strokeWidth || 0) > 0;

    if (fill !== 'none') {
        ctx.fill(path);
    }
    if (shouldStroke) {
        ctx.lineWidth = Number(attrs.strokeWidth || 1);
        ctx.lineCap = (attrs.strokeLinecap as CanvasLineCap) || 'butt';
        ctx.stroke(path);
    }
}

function drawPoint(
    ctx: CanvasRenderingContext2D,
    pointImg: IPointImage,
    transform: {
        x: number;
        y: number;
        rotate?: number;
        scale?: number;
        offsetX?: number;
        offsetY?: number;
        flipX?: boolean;
    },
) {
    if (!pointImg.element.tag) return;

    ctx.save();
    ctx.translate(transform.x, transform.y);
    if (transform.rotate) ctx.rotate((transform.rotate * Math.PI) / 180);
    const scale = transform.scale ?? 1;
    ctx.scale(scale, scale);
    ctx.translate(transform.offsetX || 0, transform.offsetY || 0);
    if (transform.flipX) ctx.scale(-1, 1);
    ctx.setLineDash([]);
    drawPointImage(ctx, pointImg);
    ctx.restore();
}

export async function prepareLabelLayout(layer: ILayerProps, identity: string): Promise<IMarkupLabelLayout | null> {
    const { label } = layer.data as IMarkupLayerProps;
    const renderText = LabelKit.getRenderText(label);
    if (!renderText) return null;

    const framework = getFramework(identity);
    await prepareMarkupLayerDocumentFonts(framework.service, layer);
    const layout = createMarkupLabelLayout(layer);
    if (!layout) return null;

    if (label.width !== layout.width || label.height !== layout.height) {
        const originLayer = cloneDeep(layer);
        label.width = layout.width;
        label.height = layout.height;
        LabelKit.updateRoundWeight(layer);
        fixDrawResult(layer, originLayer);
    }
    setLayerRuntimeValue(layer, { __markupLabelLayout: layout });

    return layout;
}

function drawArrow(
    ctx: CanvasRenderingContext2D,
    layer: ILayerProps,
    startPointImg: IPointImage,
    endPointImg: IPointImage,
) {
    const { width } = layer;
    const { markupType, fill, anchor, hasLine, label, startPoint, endPoint, centerOffset } =
        layer.data as IMarkupLayerProps;
    const halfFillWidth = fill.width / 2;

    let startPointFixOffset = 0;
    let endPointFixOffset = 0;
    const fix = (20 / 100) * fill.width;
    if (startPoint.type === MarkupPointType.StraightArrow) startPointFixOffset = fix;
    if (endPoint.type === MarkupPointType.StraightArrow) endPointFixOffset = fix;
    if (markupType === MarkupType.Arrow && startPoint.type === MarkupPointType.SolidCircle) {
        startPointFixOffset = 1;
        endPointFixOffset = 1;
    }

    const drawOffset = hasLine ? fill.width : 0;
    const startPos = {
        x: drawOffset + (startPoint.offset / MARKUP_POINT_SIZE) * fill.width + startPointFixOffset,
        y: halfFillWidth,
    };
    const endPos = {
        x: width - drawOffset - (endPoint.offset / MARKUP_POINT_SIZE) * fill.width - endPointFixOffset,
        y: halfFillWidth,
    };

    const labelRenderInfo = LabelKit.getLabelRenderInfoByLayer(layer);
    const renderText = LabelKit.getRenderText(label);
    let drawXEnd = endPos.x;
    let drawXStart = startPos.x;
    if (label.position === MarkupTextPositionType.Center && renderText) {
        const labelBoxWidth = labelRenderInfo.renderArea.width;
        const halfLineWidth = Math.ceil((width - labelBoxWidth) / 2);
        drawXEnd = Math.max(halfLineWidth - halfFillWidth, startPos.x);
        drawXStart = Math.min(halfLineWidth + halfFillWidth + labelBoxWidth, endPos.x);
    }

    ctx.save();
    ctx.translate(centerOffset.x, centerOffset.y - halfFillWidth);
    ctx.lineWidth = fill.width;
    ctx.lineCap = fill.linecap as CanvasLineCap;
    setStrokeDash(ctx, fill.dasharray, fill.width);

    if (hasLine) drawLineRect(ctx, anchor.arrowLeft, 0, -anchor.arrowLeft?.[0] + halfFillWidth, fill.width);
    drawPoint(ctx, startPointImg, {
        x: drawOffset + startPointFixOffset,
        y: -(startPointImg.height - fill.width) / 2,
    });

    if (drawXStart !== startPos.x || drawXEnd !== endPos.x) {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(drawXEnd, endPos.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(drawXStart, endPos.y);
        ctx.lineTo(endPos.x, endPos.y);
        ctx.stroke();
    } else {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(endPos.x, endPos.y);
        ctx.stroke();
    }

    drawPoint(ctx, endPointImg, {
        x: width - drawOffset - endPointFixOffset,
        y: -(endPointImg.height - fill.width) / 2,
        flipX: true,
    });
    if (hasLine)
        drawLineRect(ctx, anchor.arrowRight, width - fill.width, -anchor.arrowRight?.[0] + halfFillWidth, fill.width);
    ctx.restore();
}

function drawSegment(
    ctx: CanvasRenderingContext2D,
    layer: ILayerProps,
    startPointImg: IPointImage,
    endPointImg: IPointImage,
) {
    const { fill, anchor, startPoint, endPoint, centerOffset } = layer.data as IMarkupLayerProps;
    const hasStartPoint = !!startPointImg.element.tag;
    const hasEndPoint = !!endPointImg.element.tag && !!anchor.points![1];

    const startPointInfo = { deg: 0, point: anchor.points![0], scale: 1 };
    if (hasStartPoint) {
        startPointInfo.deg = -getPointsBetweenAngle(anchor.points![0], { x: 1, y: 0 }) + 180;
        startPointInfo.point = calculateNewCoordinates(
            anchor.points![0],
            startPointInfo.deg,
            -(startPoint.offset / MARKUP_POINT_SIZE) * fill.width * startPointInfo.scale,
        );
    }

    const endPointInfo = { deg: 0, point: anchor.points![1], scale: 0 };
    if (hasEndPoint) {
        endPointInfo.scale = startPointInfo.scale;
        endPointInfo.deg = -getPointsBetweenAngle(anchor.points![1], { x: 1, y: 0 });
        endPointInfo.point = calculateNewCoordinates(
            anchor.points![1],
            endPointInfo.deg,
            (endPoint.offset / MARKUP_POINT_SIZE) * fill.width * endPointInfo.scale,
        );
    }

    const drawPoints = [startPointInfo.point, { x: 0, y: 0 }];
    if (endPointInfo.point) drawPoints.push(endPointInfo.point);

    const offsetX = -Math.min(...drawPoints.map((p) => p.x));
    const offsetY = -Math.min(...drawPoints.map((p) => p.y));
    const newPoints = drawPoints.map((point) => ({
        x: point.x + offsetX,
        y: point.y + offsetY,
    }));

    ctx.save();
    ctx.translate(centerOffset.x - newPoints[1].x, centerOffset.y - newPoints[1].y);
    ctx.lineWidth = Math.max(fill.width * startPointInfo.scale, 1);
    ctx.lineCap = fill.linecap as CanvasLineCap;
    ctx.lineJoin = 'round';
    setStrokeDash(ctx, fill.dasharray, fill.width);

    if (hasStartPoint) {
        drawPoint(ctx, startPointImg, {
            x: anchor.points![0].x + offsetX,
            y: anchor.points![0].y + offsetY,
            rotate: startPointInfo.deg,
            scale: startPointInfo.scale,
            offsetX: -fill.width / 2,
            offsetY: -startPointImg.height / 2,
        });
    }

    ctx.beginPath();
    newPoints.forEach((point, index) => {
        if (index === 0) {
            ctx.moveTo(point.x, point.y);
            return;
        }
        ctx.lineTo(point.x, point.y);
    });
    ctx.stroke();

    if (hasEndPoint) {
        drawPoint(ctx, endPointImg, {
            x: anchor.points![1].x + offsetX,
            y: anchor.points![1].y + offsetY,
            rotate: endPointInfo.deg,
            scale: endPointInfo.scale,
            offsetX: fill.width / 2,
            offsetY: -endPointImg.height / 2,
            flipX: true,
        });
    }
    ctx.restore();
}

export class MarkupCreator {
    static async create(layer: ILayerProps, identity: string, scale = 1) {
        const labelLayout = await prepareLabelLayout(layer, identity);
        const { width, height } = layer;
        const { markupType, fill, startPoint, endPoint } = layer.data as IMarkupLayerProps;

        const originImgWidth = toInteger(width * scale);
        const originImgHeight = toInteger(height * scale);

        let loadSvgError = false;
        try {
            if (startPoint.url) await fetchVectorText(startPoint.url);
            if (endPoint.url) await fetchVectorText(endPoint.url);
        } catch (error) {
            console.log('load svg error', error);
            loadSvgError = true;
        }

        if (loadSvgError) return createCanvas(1, 1).canvas;

        const isArrow = markupType === MarkupType.Arrow;
        const isSegment = markupType === MarkupType.Segment;
        const drawPoint = isArrow || isSegment;
        const startPointImg: IPointImage =
            drawPoint && getVectorTextFromCache(startPoint.url)
                ? getPointImage(getVectorTextFromCache(startPoint.url), startPoint, fill)
                : (EmptyPointImage as IPointImage);
        const endPointImg: IPointImage =
            drawPoint && getVectorTextFromCache(endPoint.url)
                ? getPointImage(getVectorTextFromCache(endPoint.url), endPoint, fill)
                : (EmptyPointImage as IPointImage);

        const { canvas, ctx } = createCanvas(originImgWidth, originImgHeight);
        ctx.scale(scale, scale);
        const { rgba, rgb } = getRgbColor(fill.color);
        ctx.globalAlpha = rgba.a;
        ctx.fillStyle = rgb;
        ctx.strokeStyle = rgb;

        if (isArrow) {
            drawArrow(ctx, layer, startPointImg, endPointImg);
        } else if (isSegment) {
            drawSegment(ctx, layer, startPointImg, endPointImg);
        }

        ctx.globalAlpha = 1;
        drawMarkupLabelToCanvas(ctx, layer, labelLayout);

        return canvas;
    }
}
